import { SDK_ColumnTitle, strings } from "@quickpoint.me/shared";
import { type ReactElement, memo, useContext, useRef } from "react";
import styled, { type DefaultTheme, ThemeProvider } from "styled-components";
import { elementAlign, opacityHelper } from "../../lib/cssHelpers";
import { FilterColors, FilterFonts } from "../../state/filter";
import {
	PageElementsFamily,
	PageSizeAtom,
	createLocation,
} from "../../state/page";
import { HostId, IsPreviewGenerator, TapDirection } from "../../state/ui";

import { useRecoilValue } from "recoil";
import type { ParagraphProps } from "../../@types/viewerati";
import type { ElementProps } from "../../functionalComponents/RenderPage";
import useAiAnimation from "../../lib/animations/aiAnimationHook";
import { useElementAnimation } from "../../lib/animations/elementAnimationHook";
import useTappedAnimation from "../../lib/animations/tappedAnimationHook";
import createTestId from "../../lib/createTestId";
import { mainCssRules } from "../../lib/cssTemplates";
import MarkDownToTag from "../../lib/markdown/MarkDown";
import modifierStyled from "../../lib/modifierStyled";
import { NeuroDivergentFontMultiplier } from "../../state/accessibility";
import { OwnerName } from "../../state/selectors";
import { IsInFlexBoxContext } from "../displayElements/Cell";
import { PositionContext } from "../displayElements/DisplayElements";

const columnTitleFilter = (theme: DefaultTheme): SDK_ColumnTitle => {
	return theme.columnTitle;
};

const StyledColumnTitle = styled.div<ParagraphProps & { $fontMultiplier: number; $baseFontSizeRaw: number }>`
  ${(props) => elementAlign(props.theme.align, props.$isInFlexBox)};
  visibility: ${(props) => (props.$tapped ? "visible" : "hidden")};
  opacity: ${(props) => opacityHelper(props.$tapped, props.theme.opacity)};
  position: relative;
${(props) =>
	`${mainCssRules({
		baseFontSize: props.$baseFontSize,
		baseFontSizeRaw: props.$baseFontSizeRaw,
		fonts: props.$fonts,
		theme: props.theme,
		fontMultiplier: props.$fontMultiplier,
	})}`};
  ${(props) => `${modifierStyled("columnTitle:", props)}`};
  text-align: start;
`;

const ColumnTitle = ({
	atomId,
	pageIndex,
	type,
}: ElementProps): ReactElement<ElementProps> => {
	const key = createLocation(pageIndex, atomId);
	const elementData = useRecoilValue(PageElementsFamily(key)).data;
	const animation = elementData.modifier?.["animate:"];
	const direction = useRecoilValue(TapDirection);
	const isInFlexBox = useContext(IsInFlexBoxContext);
	const elRef = useRef(null);
	const filterColors = useRecoilValue(FilterColors);
	const fontMultiplier = useRecoilValue(NeuroDivergentFontMultiplier);
	const fonts = useRecoilValue(FilterFonts);
	const isPreviewer = useRecoilValue(IsPreviewGenerator);
	const ownerName = useRecoilValue(OwnerName);
	const pageSize = useRecoilValue(PageSizeAtom);
	const tapped = elementData.tapped;
	const position = useContext(PositionContext);
	const hostId = useRecoilValue(HostId);

	useTappedAnimation({ elRef, tapped, atomId });
	useAiAnimation({ elRef, modifier: elementData.modifier });
	useElementAnimation({
		ref: elRef,
		tweenName: animation,
		onePercent: {
			width: pageSize.onePercentWidth,
			height: pageSize.onePercentHeight,
		},
		options: {
			position: elementData.modifier?.["animateStart:"]
				? elementData.modifier?.["animateStart:"]
				: undefined,
		},
		position,
		direction,
	});

	const baseFontSize = pageSize.baseFontSize;

	const content =
		typeof elementData.content === "string"
			? MarkDownToTag({
					text: elementData.content,
					ownerName,
					modifier: elementData.modifier,
					hostId,
					tapped,
				})
			: elementData.content;
	if (!elementData.content) return <></>;
	return (
		<ThemeProvider theme={columnTitleFilter}>
			<StyledColumnTitle
				$baseFontSize={baseFontSize}
				$baseFontSizeRaw={pageSize.baseFontSizeRaw}
				$elementIndex={atomId}
				$filterColors={filterColors}
				$fonts={fonts}
				$fontMultiplier={fontMultiplier}
				$isInFlexBox={isInFlexBox}
				$modifier={elementData.modifier}
				$onePercentHeight={pageSize.onePercentHeight}
				$onePercentWidth={pageSize.onePercentWidth}
				$pageSize={pageSize}
				$isInPortrait={!pageSize.landscape}
				$tapped={tapped}
				aria-hidden={!elementData.tapped}
				className={`${strings.css.pageElement}  column-title`}
				data-atom-id={atomId}
				data-testid={createTestId({ atomId, position, type, isPreviewer })}
				ref={elRef}
			>
				{content}
			</StyledColumnTitle>
		</ThemeProvider>
	);
};

export default memo(ColumnTitle);
